import { FC } from "react";
import {
  Box,
  Container,
  Flex,
  Grid,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { HomeContent } from "@/constants/landingPages";

interface WidgetShowcaseProps {
  content: HomeContent["widgetShowcase"];
}

export const WidgetShowcase: FC<WidgetShowcaseProps> = ({ content }) => (
  <Box py={{ base: "16", md: "24" }} bg="white" position="relative" overflow="hidden">
    <Container maxW="7xl" position="relative" zIndex={1}>
      <VStack spacing="16">
        {/* Section header */}
        <VStack spacing="4" textAlign="center" maxW="900px" mx="auto">
          <Heading as="h2" textStyle="heading36" color="gray.900">
            {content.title.part1}{" "}
            <Text
              as="span"
              bgGradient="linear(to-r, primary, pink.400)"
              bgClip="text"
            >
              {content.title.part2}
            </Text>
          </Heading>
          <Text textStyle="heading20" color="gray.600" lineHeight="1.7">
            {content.subtitle}
          </Text>
        </VStack>

        {/* Widget comparison */}
        <Box w="100%" position="relative">
          <Grid
            templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }}
            gap="12"
            w="100%"
          >
            {/* Standard widget */}
            <ScrollReveal delay={0}>
              <Box position="relative">
                <VStack spacing="2" align="stretch">
                  <Box textAlign="center" py="4">
                    <Text
                      fontSize="xl"
                      fontWeight="800"
                      color="gray.400"
                      textTransform="uppercase"
                      letterSpacing="widest"
                    >
                      {content.comparison.standard}
                    </Text>
                  </Box>

                  <Box
                    bg="white"
                    borderRadius="2xl"
                    p="8"
                    boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                    border="1px solid"
                    borderColor="gray.100"
                    position="relative"
                    _before={{
                      content: '""',
                      position: "absolute",
                      inset: "-1px",
                      borderRadius: "2xl",
                      padding: "1px",
                      background: "linear-gradient(135deg, #f5f5f5, #e5e5e5)",
                      WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  >
                    <VStack spacing="5" align="stretch">
                      <Flex justify="space-between" align="center" pb="4" borderBottom="1px solid" borderColor="gray.100">
                        <Text fontSize="md" fontWeight="600" color="gray.500" letterSpacing="tight">
                          BookingTool
                        </Text>
                        <Box w="8" h="8" bg="gray.200" borderRadius="lg" />
                      </Flex>

                      <VStack spacing="4" align="stretch">
                        <Box>
                          <Text fontSize="xs" color="gray.500" mb="2" fontWeight="500" letterSpacing="wide">
                            {content.comparison.selectDate}
                          </Text>
                          <Box
                            h="10"
                            bg="gray.50"
                            borderRadius="lg"
                            border="1px solid"
                            borderColor="gray.200"
                            transition="all 0.2s"
                          />
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500" mb="2" fontWeight="500" letterSpacing="wide">
                            {content.comparison.selectTime}
                          </Text>
                          <Box
                            h="10"
                            bg="gray.50"
                            borderRadius="lg"
                            border="1px solid"
                            borderColor="gray.200"
                            transition="all 0.2s"
                          />
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500" mb="2" fontWeight="500" letterSpacing="wide">
                            {content.comparison.yourName}
                          </Text>
                          <Box
                            h="10"
                            bg="gray.50"
                            borderRadius="lg"
                            border="1px solid"
                            borderColor="gray.200"
                            transition="all 0.2s"
                          />
                        </Box>
                      </VStack>

                      <Box
                        h="12"
                        bg="gray.400"
                        borderRadius="lg"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mt="2"
                      >
                        <Text fontSize="sm" color="white" fontWeight="600" letterSpacing="wide">
                          {content.comparison.continue}
                        </Text>
                      </Box>
                    </VStack>
                    <Flex justify="center" mt="4">
                      <Text fontSize="xs" color="gray.500">
                        {content.comparison.poweredBy}
                      </Text>
                    </Flex>
                  </Box>

                  <VStack spacing="3" pt="4" px="2">
                    <Flex align="center" gap="3" w="100%">
                      <Box
                        w="5"
                        h="5"
                        borderRadius="full"
                        bg="red.50"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Text fontSize="xs" color="red.500" fontWeight="700">✕</Text>
                      </Box>
                      <Text fontSize="sm" color="gray.600" fontWeight="500">{content.comparison.foreignLogo}</Text>
                    </Flex>
                    <Flex align="center" gap="3" w="100%">
                      <Box
                        w="5"
                        h="5"
                        borderRadius="full"
                        bg="red.50"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Text fontSize="xs" color="red.500" fontWeight="700">✕</Text>
                      </Box>
                      <Text fontSize="sm" color="gray.600" fontWeight="500">{content.comparison.fixedDesign}</Text>
                    </Flex>
                    <Flex align="center" gap="3" w="100%">
                      <Box
                        w="5"
                        h="5"
                        borderRadius="full"
                        bg="red.50"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Text fontSize="xs" color="red.500" fontWeight="700">✕</Text>
                      </Box>
                      <Text fontSize="sm" color="gray.600" fontWeight="500">{content.comparison.genericColors}</Text>
                    </Flex>
                  </VStack>
                </VStack>
              </Box>
            </ScrollReveal>

            {/* FlexiBooking widget */}
            <ScrollReveal delay={200}>
              <Box position="relative">
                <VStack spacing="2" align="stretch">
                  <Box textAlign="center" py="4">
                    <Text
                      fontSize="xl"
                      fontWeight="800"
                      bgGradient="linear(to-r, primary, pink.500)"
                      bgClip="text"
                      textTransform="uppercase"
                      letterSpacing="widest"
                    >
                      {content.comparison.flexibooking}
                    </Text>
                  </Box>

                  <Box
                    bg="white"
                    borderRadius="2xl"
                    boxShadow="2xl"
                    p="10"
                    border="1px solid"
                    borderColor="gray.200"
                    position="relative"
                    _before={{
                      content: '""',
                      position: "absolute",
                      inset: "-2px",
                      borderRadius: "2xl",
                      padding: "2px",
                      background: "linear-gradient(135deg, rgba(255,0,0,0.3), rgba(0,0,255,0.3))",
                      WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                      opacity: 0.5,
                    }}
                  >
                    <VStack spacing="6" align="stretch">
                      <Flex justify="space-between" align="center">
                        <Text fontSize="md" fontWeight="700" color="gray.900">
                          {content.comparison.yourBrand}
                        </Text>
                        <Box
                          w="8"
                          h="8"
                          bgGradient="linear(to-br, primary, pink.500)"
                          borderRadius="md"
                        />
                      </Flex>

                      <VStack spacing="4" align="stretch">
                        <Box>
                          <Text fontSize="xs" color="gray.600" mb="2" fontWeight="500">
                            Vyberte dátum
                          </Text>
                          <Box h="12" bg="gray.100" borderRadius="lg" />
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.600" mb="2" fontWeight="500">
                            Vyberte čas
                          </Text>
                          <Box h="12" bg="gray.100" borderRadius="lg" />
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.600" mb="2" fontWeight="500">
                            Vaše meno
                          </Text>
                          <Box h="12" bg="gray.100" borderRadius="lg" />
                        </Box>
                      </VStack>

                      <Box
                        h="14"
                        bgGradient="linear(to-r, primary, pink.500)"
                        borderRadius="lg"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mt="2"
                      />
                    </VStack>
                  </Box>

                  <VStack spacing="3" pt="4" px="2">
                    <Flex align="center" gap="3" w="100%">
                      <Box
                        w="5"
                        h="5"
                        borderRadius="full"
                        bgGradient="linear(to-br, green.400, green.500)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        boxShadow="0 2px 8px rgba(34, 197, 94, 0.3)"
                      >
                        <Text fontSize="xs" color="white" fontWeight="800">✓</Text>
                      </Box>
                      <Text fontSize="sm" color="gray.900" fontWeight="600">Váš brand</Text>
                    </Flex>
                    <Flex align="center" gap="3" w="100%">
                      <Box
                        w="5"
                        h="5"
                        borderRadius="full"
                        bgGradient="linear(to-br, green.400, green.500)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        boxShadow="0 2px 8px rgba(34, 197, 94, 0.3)"
                      >
                        <Text fontSize="xs" color="white" fontWeight="800">✓</Text>
                      </Box>
                      <Text fontSize="sm" color="gray.900" fontWeight="600">{content.comparison.customDesign}</Text>
                    </Flex>
                    <Flex align="center" gap="3" w="100%">
                      <Box
                        w="5"
                        h="5"
                        borderRadius="full"
                        bgGradient="linear(to-br, green.400, green.500)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        boxShadow="0 2px 8px rgba(34, 197, 94, 0.3)"
                      >
                        <Text fontSize="xs" color="white" fontWeight="800">✓</Text>
                      </Box>
                      <Text fontSize="sm" color="gray.900" fontWeight="600">{content.comparison.yourColors}</Text>
                    </Flex>
                  </VStack>
                </VStack>
              </Box>
            </ScrollReveal>
          </Grid>
        </Box>

        {/* Custom features grid */}
        <Box w="100%" mt="8">
          <VStack spacing="8">
            <Heading as="h3" textStyle="heading24" color="gray.900" textAlign="center">
              {content.customFeatures.title}
            </Heading>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
              gap="6"
              w="100%"
            >
              {content.customFeatures.items.map((feature) => (
                <Box
                  key={feature.id}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                  p="6"
                  minH="160px"
                  display="flex"
                  flexDirection="column"
                  transition="all 0.3s"
                  _hover={{
                    borderColor: "primary",
                    transform: "translateY(-2px)",
                    boxShadow: "md",
                  }}
                >
                  <VStack align="flex-start" spacing="2">
                    <Text fontWeight="700" fontSize="md" color="gray.900">
                      {feature.title}
                    </Text>
                    <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                      {feature.description}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Box>

        {/* Bottom highlight */}
        <Box
          bgGradient="linear(to-r, primary, pink.500)"
          color="white"
          borderRadius="2xl"
          p={{ base: "8", md: "12" }}
          textAlign="center"
          w="100%"
          boxShadow="2xl"
        >
          <VStack spacing="4">
            <Heading as="h3" textStyle="heading36">
              {content.customFeatures.bottomHighlight.title}
            </Heading>
            <Text fontSize="lg" maxW="700px" opacity="0.95">
              {content.customFeatures.bottomHighlight.description}
            </Text>
          </VStack>
        </Box>
      </VStack>
    </Container>
  </Box>
);
